import { StripeCheckoutValue } from '@stripe/react-stripe-js/checkout';
import { recordError } from '@util/Errors';
import { makeUserFriendlyError, UserFriendlyError } from '@util/UserFriendlyError';
import { finalisePendingPayment } from '../../../helpers/FinalisePayment';
import { Environment } from '../../../util/Environment';
import { ActionType } from '../../ActionTypes';
import { PricingSelector } from '../../pricing/PricingActions';
import { store as baseStore } from '../../Store';

/** Confirm a Stripe Checkout session. */
export async function purchase(checkout: StripeCheckoutValue, store = baseStore): Promise<void> {
  const state = store.getState();
  const {
    purchase,
    user: { userId },
    pricing: { couponCode, currency },
  } = state;

  if (
    userId !== undefined &&
    purchase.intent.clientSecret !== undefined &&
    purchase.intent.checkoutId !== undefined &&
    purchase.checkoutId === undefined
  ) {
    try {
      store.dispatch({ type: ActionType.PAYMENT_LOADING });

      if (Environment.E2E_TESTING) {
        // For cypress succeed the payment by dispatching a success action
        const prices = PricingSelector.prices(state);
        store.dispatch({
          type: ActionType.PAYMENT_SUCCESS,
          result: {
            checkoutId: purchase.intent.checkoutId,
            paymentMethod: 'card',
            initialPlanPrice: prices?.initialPlanPrice.value ?? 0,
            couponCode,
            currency,
            isRecurring: prices?.isRecurring ?? true,
          },
        });
      } else {
        // Payment methods that redirect (Afterpay, Zip, Klarna, Cash App Pay) are off in Stripe, as
        // nothing restores the store on return. Stripe still asks for a return URL.
        const confirmResult = await checkout.confirm({
          redirect: 'if_required',
          returnUrl: location.href,
        });

        if (confirmResult.type === 'error') {
          throw new UserFriendlyError(confirmResult.error.message);
        }

        await finalisePendingPayment(store);
      }
    } catch (rawError) {
      const FALLBACK_ERROR_MESSAGE =
        'Something went wrong while processing your purchase. You have not been charged. Please try again or contact support if this issue persists.';
      const error = makeUserFriendlyError(rawError, FALLBACK_ERROR_MESSAGE);

      if (error.cause !== undefined) {
        // Ignore user errors without a cause (e.g. simple validation errors)
        recordError(error);
      }
      store.dispatch({
        type: ActionType.PAYMENT_FAILURE,
        error,
        paymentMethod: 'unknown',
      });
    }
  }
}
